import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useIsHomeHealthEnabled } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthEnabled";
import { useIsHomeHealthWorkplaceMobileExperienceEnabled } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthWorkplaceMobileExperienceEnabled";
import { useHelpCenterEntry } from "@src/appV2/HelpCenter/useHelpCenterEntry";
import { prefetchScheduleData } from "@src/appV2/redesign/DailyView/prefetchScheduleData";
import { preloadScheduleRoute } from "@src/appV2/redesign/DailyView/preloadSchedule";
import { useIsRedesignEnabled } from "@src/appV2/redesign/hooks/useIsRedesignEnabled";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { AdminMenuPrivileges } from "../privilegesProviders/AdminMenuPrivilegesProvider";
import { WorkerMenuPrivileges } from "../privilegesProviders/WorkerMenuPrivilegesProvider";
import { WorkplaceMenuPrivileges } from "../privilegesProviders/WorkplaceMenuPrivilegesProvider";
import { NavigationMenu } from "./types";
import { useNavigationTranslationLabels } from "./translationLabels";
import { getAdminNavigationMenu } from "./useGetAdminNavigationMenu";
import { getWorkerNavigationMenu } from "./useGetWorkerNavigationMenu";
import { getWorkplaceNavigationMenu } from "./useGetWorkplaceNavigationMenu";

interface NavigationMenuOptions {
  unreadChannelCount?: number;
  pendingExtraWorkedTimeRequestCount?: number;
  pendingVisitOccurrenceCount?: number;
  isMobile?: boolean;
}

export function useGetNavigationMenu(
  isAdmin: boolean,
  adminMenuPrivileges: AdminMenuPrivileges,
  isWorker: boolean,
  workerMenuPrivileges: WorkerMenuPrivileges,
  isWorkplace: boolean,
  workplaceMenuPrivileges: WorkplaceMenuPrivileges,
  currentPath: string,
  options: NavigationMenuOptions = {}
): NavigationMenu {
  const analyticsConfig = useCbhFlag(CbhFeatureFlag.FACILITY_EXPLO_ANALYTICS_ENABLED, {
    defaultValue: [],
  });
  const isRedesignEnabled = useIsRedesignEnabled();
  const isHomeHealthEnabled = useIsHomeHealthEnabled();
  const isHomeHealthWorkplaceMobileExperienceEnabled =
    useIsHomeHealthWorkplaceMobileExperienceEnabled();
  const queryClient = useQueryClient();
  const { profile } = useSession();
  const translationLabels = useNavigationTranslationLabels();
  const helpCenterEntry = useHelpCenterEntry();
  const preloadSchedule = useCallback(() => {
    preloadScheduleRoute();

    if (!isWorkplace || !workplaceMenuPrivileges.dailyView) {
      return;
    }

    void prefetchScheduleData({
      queryClient,
      facilityId: profile.userId,
      timezone: profile.tmz,
    });
  }, [isWorkplace, workplaceMenuPrivileges.dailyView, queryClient, profile.userId, profile.tmz]);

  if (isAdmin) {
    return getAdminNavigationMenu(adminMenuPrivileges, currentPath, translationLabels);
  } else if (isWorker) {
    return getWorkerNavigationMenu(
      workerMenuPrivileges,
      currentPath,
      profile.userId,
      translationLabels
    );
  } else if (isWorkplace) {
    return getWorkplaceNavigationMenu(workplaceMenuPrivileges, currentPath, analyticsConfig, {
      unreadChannelCount: options.unreadChannelCount,
      pendingExtraWorkedTimeRequestCount: options.pendingExtraWorkedTimeRequestCount,
      pendingVisitOccurrenceCount: options.pendingVisitOccurrenceCount,
      isMobile: options.isMobile,
      isHomeHealthWorkplaceMobileExperienceEnabled,
      isClassicCalendarPathActive: isHomeHealthEnabled || isRedesignEnabled,
      onSchedulePreload: preloadSchedule,
      translationLabels,
      helpCenterEntry,
    });
  } else {
    return { items: [] };
  }
}
